import React from 'react';
import { GitCompare, AlertCircle, Info } from 'lucide-react';
import { useBulleAuSurvol } from '../BulleSurvol';

/**
 * Nos leviers en regard de ceux du concurrent.
 *
 * ## ⚠️ Un inventaire d'audiences n'est pas un usage
 *
 * C'est la distinction qui fait tout l'intérêt de ce panneau, et elle est mesurée : le compte
 * Meta de Pébéo porte **six audiences personnalisées** — deux en retargeting site, deux en
 * engagement, deux en audience similaire — et **aucun** de ses cinq ensembles actifs ne les
 * cible. Les cinq tournent sur des centres d'intérêt.
 *
 * Compter l'inventaire comme de l'usage aurait donc annoncé « nous activons le retargeting
 * site, l'engagement et le lookalike » alors que nous n'en diffusons aucun. L'écart réel est
 * l'inverse : les audiences sont là, et personne ne s'en sert.
 *
 * D'où deux colonnes nommées séparément, et un « actif » qui ne se juge que sur l'usage.
 *
 * ## ⚠️ Un refus de lecture n'est pas un zéro
 *
 * Un compte que Meta refuse ne rend **pas** une liste vide : le serveur rend un motif, et ce
 * panneau l'affiche. Sans cela, un parc inaccessible se lirait « nous n'activons rien », et
 * l'on conclurait à un retard sur le concurrent alors qu'on n'a simplement pas pu lire.
 *
 * Et le bouton de reconnexion ne paraît **que si elle sert** (`reconnexionUtile`) : sur un refus
 * d'habilitation, elle ferait défaire un réglage intact qui rendra le même refus.
 */

const ETAT_CONCURRENT = {
  confirme: { libelle: 'confirmé', couleur: '#065f46' },
  indice: { libelle: 'indice', couleur: '#92400e' },
  absent_malgre_parcours: { libelle: 'non activé', couleur: '#1d4ed8' },
  non_observe: { libelle: 'non observé', couleur: '#9ca3af' }
};

const NosLeviers = ({ nos, synthese, onReconnecter }) => {
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  if (!nos) return null;

  const leviersObserves = new Map((synthese?.leviers || []).map(l => [l.levier, l]));

  // Ne montrer que les leviers qui disent quelque chose d'un côté ou de l'autre : les dix lignes
  // « rien chez lui, rien chez nous » n'apprendraient rien et noieraient les écarts.
  const lignes = (synthese?.leviers || [])
    .map(l => ({ ...l, nous: nos.parLevier?.[l.levier] }))
    .filter(l => l.observations > 0
      || l.confiance.cle === 'absent_malgre_parcours'
      || l.nous?.actif
      || l.nous?.audiences > 0);

  return (
    <div style={{
      padding: '20px', borderRadius: '12px', backgroundColor: '#fff',
      border: '1px solid #e5e7eb', marginBottom: '20px'
    }}>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '600' }}>
        <GitCompare size={16} />
        Ses leviers, et les nôtres
      </h3>
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', marginBottom: '16px' }}>
        À gauche ce qu'on a relevé chez lui, à droite ce que nous diffusons réellement
      </p>

      {/* ⚠️ L'absence nommée, et le chemin pour la lever. */}
      {nos.absence && (
        <div style={{
          display: 'flex', gap: '10px', padding: '14px', borderRadius: '8px',
          backgroundColor: '#f9fafb', border: '1px dashed #d1d5db', marginBottom: '16px'
        }}>
          <Info size={16} style={{ color: '#6b7280', flexShrink: 0, marginTop: '1px' }} />
          <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5, margin: 0 }}>
            {nos.absence.message}
          </p>
        </div>
      )}

      {/* Le refus de chaque compte, avec son motif — et le bouton seulement s'il sert. */}
      {(nos.comptes || []).filter(c => c.refus).map(c => (
        <div key={c.libraryItemId} style={{
          display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '12px 14px',
          borderRadius: '8px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', marginBottom: '12px'
        }}>
          <AlertCircle size={15} style={{ color: '#92400e', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#92400e' }}>{c.nom}</div>
            <div style={{ fontSize: '12px', color: '#92400e', lineHeight: 1.45 }}>{c.refus.message}</div>
          </div>
          {/* ⚠️ Seulement si la reconnexion répare : sur un refus d'habilitation elle ferait
              défaire un réglage intact, qui rendra le même refus. */}
          {c.refus.reconnexionUtile && onReconnecter && (
            <button onClick={onReconnecter} style={{
              padding: '6px 12px', borderRadius: '6px', border: '1px solid #92400e',
              backgroundColor: '#fff', color: '#92400e', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap'
            }}>
              Reconnecter
            </button>
          )}
        </div>
      ))}

      {lignes.length === 0 ? (
        <p style={{ fontSize: '13px', color: '#9ca3af', lineHeight: 1.5 }}>
          Rien à comparer pour l'instant : aucun relevé chez ce concurrent, et aucun levier actif
          de notre côté.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {lignes.map(l => {
            const etat = ETAT_CONCURRENT[l.confiance.cle] || ETAT_CONCURRENT.non_observe;
            const chezLui = l.observations > 0 || l.confiance.cle === 'absent_malgre_parcours';
            const chezNous = l.nous?.actif === true;
            // L'écart dans les deux sens : c'est ce qu'on vient chercher.
            const ecart = chezLui && l.confiance.cle !== 'absent_malgre_parcours' && !chezNous
              ? 'lui_seul'
              : (!chezLui && chezNous ? 'nous_seuls' : null);

            return (
              <div
                key={l.levier}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: '12px',
                  alignItems: 'center',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: ecart ? '#fffbeb' : '#f9fafb',
                  border: `1px solid ${ecart ? '#fde68a' : '#f3f4f6'}`
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500' }}>{l.libelle}</div>
                  {ecart && (
                    <div style={{ fontSize: '11px', color: '#92400e' }}>
                      {ecart === 'lui_seul'
                        ? "il l'active, nous non"
                        : "nous l'activons, rien relevé chez lui"}
                    </div>
                  )}
                </div>

                <span
                  className="ksi-survolable"
                  onMouseEnter={survoler(l.confiance.aide)}
                  onMouseLeave={quitter}
                  style={{ fontSize: '12px', color: etat.couleur, whiteSpace: 'nowrap' }}
                >
                  Lui : {etat.libelle}
                  {l.observations > 0 && ` (${l.observations})`}
                </span>

                {/*
                  ⚠️ « Nous » se juge sur les ensembles ACTIFS. Les audiences existantes sont
                  mentionnées à côté, et jamais comptées comme un usage : six audiences créées
                  et zéro ensemble qui les cible, c'est « non activé » — mesuré sur ce compte.
                */}
                <span
                  className="ksi-survolable"
                  onMouseEnter={survoler(
                    l.nous
                      ? `${l.nous.ensemblesActifs} ensemble(s) de publicités actif(s) emploient ce `
                        + `levier, et ${l.nous.audiences} audience(s) de cette famille existent sur le `
                        + `compte.\n\n⚠️ Une audience créée et jamais ciblée n'est pas un levier activé.`
                        + (l.nous.exemples?.length ? `\n\nPar exemple : ${l.nous.exemples.join(', ')}` : '')
                      : "Nos propres leviers n'ont pas pu être lus pour ce compte."
                  )}
                  onMouseLeave={quitter}
                  style={{
                    fontSize: '12px',
                    color: chezNous ? '#065f46' : '#9ca3af',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Nous : {chezNous ? `${l.nous.ensemblesActifs} actif${l.nous.ensemblesActifs > 1 ? 's' : ''}` : 'non activé'}
                  {!chezNous && l.nous?.audiences > 0 && ` (${l.nous.audiences} aud.)`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ⚠️ Une liste coupée se dit. */}
      {nos.tronque && (
        <p style={{ fontSize: '11px', color: '#92400e', marginTop: '12px', marginBottom: 0, lineHeight: 1.45 }}>
          La lecture des ensembles de publicités a atteint son plafond : des ensembles actifs
          peuvent manquer, et un « non activé » n'est donc pas certain pour ce compte.
        </p>
      )}

      {/* ⚠️ Nommés, jamais écartés en silence : un subtype que Meta vient d'introduire doit
          apparaître quelque part. */}
      {(nos.comptes || []).some(c => c.subtypesNonClasses?.length > 0) && (
        <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px', marginBottom: 0, lineHeight: 1.45 }}>
          Types d'audience non classés :{' '}
          {[...new Set((nos.comptes || []).flatMap(c => c.subtypesNonClasses || []))].join(', ')}.
          Ils n'entrent dans aucune famille du référentiel et ne sont pas comptés.
        </p>
      )}

      {bulle}
    </div>
  );
};

export default NosLeviers;
